import { useEffect, useState } from "react";

import { api } from "../api/client";
import { Panel } from "../components/Panel";
import { Select } from "../components/Select";
import { ui } from "../lib/ui";
import { urlParam, useUrlState } from "../lib/urlState";
import { useAsync } from "../lib/useAsync";

const PAGE_SIZE = 50;

/** Settings › Database: this PC's tables as they are stored, read-only, for
 *  checking what a run or a reset really left. Passwords and hub tokens read
 *  as hidden and BLOBs (face embeddings and crops) as their size: the server
 *  never sends them. */
export function Database() {
  const [table, setTable] = useState(urlParam("table") ?? "");
  const [search, setSearch] = useState(urlParam("q") ?? "");
  const [draft, setDraft] = useState(urlParam("q") ?? "");
  const [sort, setSort] = useState(urlParam("sort") ?? "");
  const [direction, setDirection] = useState(urlParam("dir") === "asc" ? "asc" : "desc");
  const [page, setPage] = useState(Math.max(1, Number(urlParam("page")) || 1));

  useUrlState(
    {
      table,
      q: search,
      sort,
      dir: direction === "asc" ? "asc" : "",
      page: page > 1 ? String(page) : "",
    },
    (incoming) => {
      if ("table" in incoming) setTable(incoming.table);
      if ("q" in incoming) {
        setSearch(incoming.q);
        setDraft(incoming.q);
      }
      if ("sort" in incoming) setSort(incoming.sort);
      if ("dir" in incoming) setDirection(incoming.dir === "asc" ? "asc" : "desc");
      if ("page" in incoming) setPage(Math.max(1, Number(incoming.page) || 1));
    },
  );

  const tables = useAsync(() => api.databaseTables(), []);
  // Nothing chosen yet: the first table that has anything in it.
  useEffect(() => {
    if (table || !tables.data?.length) return;
    setTable((tables.data.find((t) => t.rows > 0) ?? tables.data[0]).name);
  }, [table, tables.data]);

  const rows = useAsync(
    () =>
      table
        ? api.databaseTable(table, {
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            search,
            sort,
            direction,
          })
        : Promise.resolve(null),
    [table, page, search, sort, direction],
  );

  const choose = (name: string) => {
    setTable(name);
    setSort("");
    setDirection("desc");
    setPage(1);
    setSearch("");
    setDraft("");
  };
  const sortBy = (column: string) => {
    if (sort === column) setDirection(direction === "asc" ? "desc" : "asc");
    else {
      setSort(column);
      setDirection("asc");
    }
    setPage(1);
  };
  const refresh = () => {
    tables.reload();
    rows.reload();
  };

  const data = rows.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Panel
      title="Database"
      subtitle="The tables on this PC as they are stored. Read-only: nothing here changes anything."
      about="Newest rows first unless you sort by a column. Times are UTC. Passwords and hub tokens are hidden, and face embeddings and crops show only their size."
      actions={
        <button type="button" className={ui.btnQuiet} onClick={refresh}>
          Refresh
        </button>
      }
    >
      {(tables.error || rows.error) && (
        <p className={`${ui.notice} ${ui.noticeError}`}>{tables.error ?? rows.error}</p>
      )}
      <form
        className={`${ui.toolbar} mb-4`}
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(draft.trim());
          setPage(1);
        }}
      >
        <label className={ui.field}>
          Table
          <Select
            wrapperClassName="min-w-64"
            value={table}
            onChange={(event) => choose(event.target.value)}
          >
            {tables.data?.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} ({t.rows.toLocaleString()})
              </option>
            ))}
          </Select>
        </label>
        <label className={ui.field}>
          Search this table
          <span className="flex gap-2">
            <input
              className={`${ui.input} w-64`}
              type="search"
              value={draft}
              placeholder="Any column contains…"
              onChange={(event) => setDraft(event.target.value)}
            />
            <button type="submit" className={ui.btnQuiet}>
              Search
            </button>
          </span>
        </label>
      </form>

      {data && (
        <>
          <div className="overflow-x-auto">
            <table className={ui.table}>
              <thead>
                <tr>
                  {data.columns.map((column) => (
                    <th key={column.name} className="whitespace-nowrap normal-case!" title={column.type}>
                      {column.hidden ? (
                        column.name
                      ) : (
                        <button
                          type="button"
                          className="cursor-pointer font-semibold hover:text-accent"
                          onClick={() => sortBy(column.name)}
                        >
                          {column.name}
                          {sort === column.name ? (direction === "asc" ? " ▲" : " ▼") : ""}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={index}>
                    {row.map((value, column) => (
                      <td key={column} className="font-mono text-xs">
                        {value === null ? (
                          <span className="text-muted">NULL</span>
                        ) : (
                          <div className="max-h-24 max-w-[420px] min-w-max overflow-auto whitespace-pre-wrap break-words">
                            {String(value)}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.rows.length === 0 && (
            <p className="py-6 text-center text-muted">
              {search ? "No row matches that search." : "This table is empty."}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between gap-3 text-[13px] text-muted">
            <span>
              {data.total.toLocaleString()} row{data.total === 1 ? "" : "s"}
              {search ? " match" : ""}
            </span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                className={ui.btnQuiet}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              Page {page} of {pages}
              <button
                type="button"
                className={ui.btnQuiet}
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}
